import { diaNegocio, hora12, inicioSemana, nombreServicios, serviciosDe, sumarDias, type CitaVista } from '@barbones/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { CalendarPlus, ChevronLeft, ChevronRight, UserRoundX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { Button } from '@/components/ui/button';
import { Esqueleto, Insignia, Tarjeta } from '@/components/ui/data';
import { Input } from '@/components/ui/form';
import { cn, diaLargo } from '@/lib/utils';
import { CitaDetalle } from './CitaDetalle';
import { useAgendaDia, useConfig, usePendientes } from './datos';
import { NuevaCitaDialog } from './NuevaCitaDialog';
import { VistaDia } from './VistaDia';
import { VistaSemana } from './VistaSemana';
import { useMoverCita } from './moverCita';

export interface BusquedaAgenda {
  dia?: string;
  cita?: string;
  pendientes?: boolean;
  vista?: 'dia' | 'semana';
}

interface Sucursal {
  id: string;
  apertura: string;
  cierre: string;
}

export function AgendaPage() {
  const busqueda = useSearch({ strict: false }) as BusquedaAgenda;
  const navegar = useNavigate();
  const { puede, sucursalId } = useSesion();
  const hoy = diaNegocio(new Date());
  const dia = busqueda.dia ?? hoy;
  const vista = busqueda.vista ?? 'dia';
  const horizonte = useConfig<number>('agenda.horizonteDias', 60);
  const intervalo = useConfig<number>('agenda.intervaloMinutos', 15);
  const agenda = useAgendaDia(dia);
  const pendientes = usePendientes(hoy, sumarDias(hoy, horizonte), puede('agenda.ver'));
  const sucursales = useQuery({ queryKey: ['sucursales'], queryFn: () => api<Sucursal[]>('/sucursales'), staleTime: 300_000 });
  const sucursal = sucursales.data?.find((s) => s.id === sucursalId);

  const [nueva, setNueva] = useState<{ barberoId: string | null; hora?: string } | null>(null);
  const [detalle, setDetalle] = useState<CitaVista | null>(null);
  const [verPendientes, setVerPendientes] = useState(Boolean(busqueda.pendientes));

  const ir = (cambios: Partial<BusquedaAgenda>) => void navegar({ to: '/agenda', search: { ...busqueda, cita: undefined, ...cambios } });
  const mover = (dias: number) => ir({ dia: vista === 'semana' ? sumarDias(inicioSemana(dia, 1), dias * 7) : sumarDias(dia, dias) });

  // Abre la cita indicada en la URL (desde el buscador global o una notificación).
  useEffect(() => {
    if (!busqueda.cita || !agenda.data) return;
    const c = agenda.data.citas.find((x) => x.id === busqueda.cita);
    if (c) setDetalle(c);
  }, [busqueda.cita, agenda.data]);

  const moverCita = useMoverCita(dia, agenda.data?.barberos ?? []);
  const pendientesCount = pendientes.data?.length ?? 0;
  const puedeCrear = puede('agenda.crear');

  return (
    <ModuleLayout titulo="Agenda">
      <EncabezadoPagina
        titulo="Agenda"
        descripcion={<span className="inline-block first-letter:uppercase">{vista === 'dia' ? diaLargo(dia) : `Semana del ${diaLargo(inicioSemana(dia, 1))}`}</span>}
        acciones={
          <>
            {puede('agenda.ver') && (
              <Button variante={verPendientes ? 'primario' : 'secundario'} onClick={() => setVerPendientes((v) => !v)} aria-pressed={verPendientes}>
                <UserRoundX />
                Por asignar
                {pendientesCount > 0 && <Insignia tono="aviso">{pendientesCount}</Insignia>}
              </Button>
            )}
            {puedeCrear && (
              <Button onClick={() => setNueva({ barberoId: null })}>
                <CalendarPlus /> Nueva cita
              </Button>
            )}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variante="secundario" tamano="icono" onClick={() => mover(-1)} aria-label="Anterior">
          <ChevronLeft />
        </Button>
        <Button variante={dia === hoy ? 'acento' : 'secundario'} onClick={() => ir({ dia: hoy })}>
          Hoy
        </Button>
        <Button variante="secundario" tamano="icono" onClick={() => mover(1)} aria-label="Siguiente">
          <ChevronRight />
        </Button>
        <Input
          type="date"
          value={dia}
          max={sumarDias(hoy, horizonte)}
          onChange={(e) => e.target.value && ir({ dia: e.target.value })}
          className="w-44"
          aria-label="Ir a fecha"
        />
        <div className="ml-auto flex rounded-md border border-border p-0.5" role="group" aria-label="Vista">
          {(['dia', 'semana'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => ir({ vista: v })}
              aria-pressed={vista === v}
              className={cn('rounded px-3 py-1 text-[13px]', vista === v ? 'bg-primary text-on-primary' : 'text-foreground-muted hover:text-foreground')}
            >
              {v === 'dia' ? 'Día' : 'Semana'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-4">
        <div className="min-w-0 flex-1">
          {vista === 'semana' ? (
            <VistaSemana semana={inicioSemana(dia, 1)} onCita={setDetalle} onDia={(d) => ir({ dia: d, vista: 'dia' })} />
          ) : agenda.isLoading || !agenda.data ? (
            <Esqueleto className="h-[600px]" />
          ) : agenda.data.barberos.length === 0 ? (
            <Tarjeta className="p-8 text-center text-[14px] text-foreground-muted">
              No hay barberos registrados. Da de alta a tu equipo en Configuración › Usuarios.
            </Tarjeta>
          ) : (
            <VistaDia
              dia={dia}
              apertura={sucursal?.apertura ?? '10:00'}
              cierre={sucursal?.cierre ?? '21:00'}
              intervaloMin={intervalo}
              barberos={agenda.data.barberos}
              citas={agenda.data.citas}
              mostrarSinAsignar={puede('agenda.ver')}
              onCita={setDetalle}
              onHueco={(barberoId, hora) => puedeCrear && setNueva({ barberoId, hora })}
              onMover={puede('agenda.reprogramar') ? moverCita.mover : undefined}
              bloqueado={moverCita.moviendo}
            />
          )}
        </div>

        {verPendientes && (
          <aside className="w-72 shrink-0" aria-label="Citas por asignar">
            <Tarjeta className="sticky top-20">
              <div className="border-b border-border px-4 py-3">
                <p className="font-display text-[16px]">Por asignar barbero</p>
                <p className="text-[12px] text-foreground-muted">Próximos {horizonte} días</p>
              </div>
              <ul className="max-h-[70vh] overflow-y-auto p-2">
                {pendientesCount === 0 && <li className="px-2 py-6 text-center text-[13px] text-foreground-muted">Todo asignado</li>}
                {pendientes.data?.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => setDetalle(c)} className="w-full rounded-md px-2 py-2 text-left hover:bg-muted">
                      <p className="text-[13px] font-medium">{c.cliente.nombre}</p>
                      <p className="cifras text-[12px] text-foreground-muted">
                        {diaLargo(diaNegocio(new Date(c.inicio)))} · {hora12(new Date(c.inicio))}
                      </p>
                      <p className="text-[12px] text-foreground-muted">{nombreServicios(serviciosDe(c))}</p>
                    </button>
                  </li>
                ))}
              </ul>
            </Tarjeta>
          </aside>
        )}
      </div>

      {nueva && <NuevaCitaDialog dia={dia} barberoInicial={nueva.barberoId} horaInicial={nueva.hora} onCerrar={() => setNueva(null)} />}
      {detalle && (
        <CitaDetalle
          key={detalle.id}
          cita={detalle}
          onCerrar={() => {
            setDetalle(null);
            if (busqueda.cita) ir({});
          }}
        />
      )}
    </ModuleLayout>
  );
}
